import React, { useContext, useCallback, useState } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { LegacyI18nContext } from '@bingads-webui-react/legacy-i18n-context';
import { BudgetInput as ValidatedBudgetInput } from '@bingads-webui-react/budget-control';
import { ErrorMessages } from '@bingads-webui-react/validation';
import { getBudgetInputStyles } from './styles/get-budget-input-styles';
import { CurrencyDropdown } from './currency-dropdown';

const RANGE_STEP = 0.01;

export const BudgetInput = ({
  budgetValue,
  currencies,
  selectedCurrency,
  showDropdown,
  isCurrencyDisabled,
  onBudgetInputChange,
  onBudgetValidate,
  onRenderInlineHelp,
  onSelectCurrency,
  schema,
}) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useContext(LegacyI18nContext);
  const classes = useDefaultStyles(getBudgetInputStyles);
  const maxBudget = _.get(schema, ['properties', 'budget', 'maximum']);
  const minBudget = _.get(schema, ['properties', 'budget', 'minimum']);
  const schemaName = _.get(schema, 'name');

  const onRenderCurrencyDropDown =
    useCallback(() => (<CurrencyDropdown
      currencies={currencies}
      selectedCurrency={selectedCurrency}
      onSelectCurrency={onSelectCurrency}
      ariaLabel={i18n.getString(_TL_('Currency'))}
    />
    ), [currencies, selectedCurrency, onSelectCurrency, i18n]);

  const [budgetError, setBudgetError] = useState([]);
  const [isBudgetValid, setIsBudgetValid] = useState(false);

  const customErrorProcessor = useCallback(errors => _.map(
    errors,
    (oldError, key) => {
      const customErrors = {
        decimalLength: i18n.getString(_TL_('Budget needs to be set to 2 decimal lengths')),
        range: i18n.getString(_TL_('Your budget should be greater than {{accountCurrencyCode}} {{dailyRangeFloorValue}} and less than your daily maximum. The daily max. is your monthly budget averaged out over the number of days left in the month.'), {
          accountCurrencyCode: selectedCurrency,
          dailyRangeFloorValue: minBudget,
        }),
        required: i18n.getString(_TL_('This field is required')),
        type: i18n.getString(_TL_('Field requires numeric value')),
      };
      return customErrors[key] || oldError;
    }
  ), [i18n, minBudget, selectedCurrency]);

  const onBudgetInputValidate = useCallback(({ data, isValid, messages }) => {
    setIsBudgetValid(isValid);
    setBudgetError(messages);
    onBudgetValidate({ data, isValid, messages });
  }, [onBudgetValidate]);

  return (
    <React.Fragment>
      <ValidatedBudgetInput
        showDropdown={showDropdown}
        i18n={i18n}
        classes={classes}
        legacyI18n={legacyI18n}
        onBudgetChange={onBudgetInputChange}
        validationKey={`${schemaName}-${maxBudget}-${minBudget}`}
        value={budgetValue}
        inputClassName={!isBudgetValid && classes.isInvalid}
        isCurrencyDisabled={isCurrencyDisabled}
        selectedCurrencyText={selectedCurrency}
        currencies={currencies}
        RANGE_STEP={RANGE_STEP}
        onBudgetInputValidate={onBudgetInputValidate}
        schema={schema}
        validationProps={{
          field: 'budget',
          errorProcessor: customErrorProcessor,
          alwaysShowError: false,
          initialValue: budgetValue,
        }}
        onSelectCurrency={onSelectCurrency}
        budgetTitle={i18n.getString(_TL_('Set your daily budget for this campaign.'))}
        onRenderCurrencyDropDown={onRenderCurrencyDropDown}
        onRenderInlineHelp={onRenderInlineHelp}
      />
      {!_.isEmpty(budgetError) ?
        <ErrorMessages className={classNames('budget-input-error', classes.budgetInputError)} messages={budgetError} /> : null}
    </React.Fragment>
  );
};

BudgetInput.propTypes = {
  budgetValue: PropTypes.string.isRequired,
  currencies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedCurrency: PropTypes.string.isRequired,
  showDropdown: PropTypes.bool.isRequired,
  isCurrencyDisabled: PropTypes.bool.isRequired,
  onBudgetInputChange: PropTypes.func.isRequired,
  onBudgetValidate: PropTypes.func.isRequired,
  onRenderInlineHelp: PropTypes.func,
  onSelectCurrency: PropTypes.func.isRequired,
  budgetSuggestion: PropTypes.shape({
    GlobalMaximumBudget: PropTypes.shape({
      DailyBudget: PropTypes.number.isRequired,
    }),
    GlobalMinimumBudget: PropTypes.shape({
      DailyBudget: PropTypes.number.isRequired,
    }),
  }).isRequired,
  schema: PropTypes.shape({
    name: PropTypes.string,
    type: PropTypes.string,
    properties: PropTypes.shape(),
  }),
};

BudgetInput.defaultProps = {
  schema: undefined,
  onRenderInlineHelp: _.noop,
};
